import { useTranslation } from "react-i18next";
import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Quotes } from "@phosphor-icons/react";
import { CASE_STUDIES, CASE_KPI_TEMPLATE } from "@/data/company";

export function CaseStudiesIndex() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  const published = CASE_STUDIES.filter((c) => c.published);

  return (
    <>
      <SEO title={isNl ? "Case Studies" : "Case Studies"} path="/case-studies" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Case studies" : "Case studies"}</Eyebrow>
          <SectionHeading
            className="mt-4"
            title={isNl ? "Echte hotels. Echte cijfers. Binnenkort." : "Real hotels. Real numbers. Coming soon."}
            sub={isNl
              ? "We publiceren geen verzonnen case studies. Zodra onze eerste pilot-hotels live gaan, verschijnen ze hier — met echte namen, cijfers en quotes."
              : "We don't publish invented case studies. As soon as our first pilot hotels go live, they'll appear here — with real names, numbers and quotes."}
          />
          {published.length === 0 && (
            <div className="mt-16 rounded-2xl border border-bht-gold/30 bg-bht-gold/5 p-10 text-center" data-testid="cases-empty">
              <div className="font-mono text-[11px] uppercase tracking-widest text-bht-gold/80">{isNl ? "Wat u hier gaat vinden" : "What you'll find here"}</div>
              <div className="mt-6 grid gap-3 md:grid-cols-3">
                {CASE_KPI_TEMPLATE.slice(0, 6).map((k, i) => (
                  <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-sm text-white/80">
                    {k[lang]}
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <GoldButton to={`/${lang}/book-demo`}>{isNl ? "Wordt onze volgende case study" : "Become our next case study"}</GoldButton>
              </div>
            </div>
          )}
          {published.length > 0 && (
            <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {published.map((c) => {
                const data = c[lang] || c.en;
                return (
                  <Link
                    key={c.slug}
                    to={`/${lang}/case-studies/${c.slug}`}
                    className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-colors hover:border-bht-gold/30"
                    data-testid={`case-${c.slug}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">{data.hotel.location}</div>
                    <div className="mt-6 font-serif text-2xl group-hover:text-bht-gold">{data.title}</div>
                    <p className="mt-3 text-sm text-white/55">{data.excerpt}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[12px] text-white/60 group-hover:text-bht-gold">
                      {isNl ? "Lees case" : "Read case"} <ArrowRight size={12} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function CaseStudyPage() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";

  const c = CASE_STUDIES.find((x) => x.slug === slug);
  if (!c) return <Navigate to={`/${lang}/case-studies`} replace />;
  const data = c[lang] || c.en;
  const s = data.sections;

  const Sec = ({ title, children, testId }) => (
    <section className="border-t border-white/[0.06] py-14" data-testid={testId}>
      <div className="grid gap-10 md:grid-cols-[220px_1fr]">
        <div className="font-mono text-[11px] uppercase tracking-widest text-bht-gold">{title}</div>
        <div className="text-base leading-relaxed text-white/75">{children}</div>
      </div>
    </section>
  );

  return (
    <>
      <SEO title={`${data.title} — Case study`} path={`/case-studies/${slug}`} />
      {/* Hero */}
      <section className="relative overflow-hidden py-24 lg:py-32">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <Link to={`/${lang}/case-studies`} className="eyebrow inline-flex items-center gap-2 hover:text-bht-gold">
            ← {isNl ? "Alle case studies" : "All case studies"}
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl"
          >
            {data.title}
          </motion.h1>
          <p className="mt-6 max-w-3xl text-lg text-white/65">{data.excerpt}</p>

          <div className="mt-10 grid gap-3 md:grid-cols-4">
            {[
              { k: isNl ? "Hotel" : "Hotel", v: data.hotel.name },
              { k: isNl ? "Type" : "Type", v: data.hotel.type },
              { k: isNl ? "Locatie" : "Location", v: data.hotel.location },
              { k: isNl ? "Kamers" : "Rooms", v: data.hotel.rooms },
            ].map((x, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="eyebrow">{x.k}</div>
                <div className="mt-2 font-serif text-lg text-white">{x.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Body sections — framework */}
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <Sec title={isNl ? "01 · Klantintroductie" : "01 · Introduction"} testId="case-intro">
          {s.introduction || <em className="text-white/40">{isNl ? "Wordt binnenkort ingevuld met echte hotelinformatie." : "Will be filled in with real hotel information soon."}</em>}
        </Sec>
        <Sec title={isNl ? "02 · Uitdagingen vóór BHT One" : "02 · Challenges before BHT One"}>
          {s.challenges?.length ? (
            <ul className="space-y-2">{s.challenges.map((it, i) => <li key={i}>• {it}</li>)}</ul>
          ) : <em className="text-white/40">—</em>}
        </Sec>
        <Sec title={isNl ? "03 · Waarom BHT One" : "03 · Why BHT One"}>
          {s.why_bht?.length ? (
            <ul className="space-y-2">{s.why_bht.map((it, i) => <li key={i}>• {it}</li>)}</ul>
          ) : <em className="text-white/40">—</em>}
        </Sec>
        <Sec title={isNl ? "04 · Implementatieproces" : "04 · Implementation"}>
          {s.implementation || <em className="text-white/40">—</em>}
        </Sec>
        <Sec title={isNl ? "05 · Modules geïmplementeerd" : "05 · Modules implemented"}>
          {s.modules?.length ? (
            <div className="flex flex-wrap gap-2">{s.modules.map((m, i) => (
              <span key={i} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs">{m}</span>
            ))}</div>
          ) : <em className="text-white/40">—</em>}
        </Sec>
        <Sec title={isNl ? "06 · Dagelijkse workflow-verbeteringen" : "06 · Daily workflow improvements"}>
          {s.daily_improvements?.length ? (
            <ul className="space-y-2">{s.daily_improvements.map((it, i) => <li key={i} className="flex items-start gap-2"><CheckCircle size={16} weight="regular" className="mt-1 shrink-0 text-bht-gold" /><span>{it}</span></li>)}</ul>
          ) : <em className="text-white/40">—</em>}
        </Sec>
        <Sec title={isNl ? "07 · Employee adoption" : "07 · Employee adoption"}>{s.adoption || <em className="text-white/40">—</em>}</Sec>
        <Sec title={isNl ? "08 · AI-gebruik" : "08 · AI usage"}>{s.ai_usage || <em className="text-white/40">—</em>}</Sec>
        <Sec title={isNl ? "09 · Kennisbank" : "09 · Knowledge Base"}>{s.knowledge_base || <em className="text-white/40">—</em>}</Sec>
        <Sec title={isNl ? "10 · Interne communicatie" : "10 · Internal communication"}>{s.internal_comm || <em className="text-white/40">—</em>}</Sec>
        <Sec title={isNl ? "11 · Website & gast-app" : "11 · Website & Guest App"}>{s.website_app || <em className="text-white/40">—</em>}</Sec>
        <Sec title={isNl ? "12 · Gemeten impact" : "12 · Measured impact"}>
          {s.impact?.length ? (
            <div className="grid gap-4 md:grid-cols-3">
              {s.impact.map((it, i) => (
                <GlassCard key={i} className="p-6">
                  <div className="font-serif text-4xl leading-none text-bht-gold">{it.value}</div>
                  <div className="eyebrow mt-4">{it.label}</div>
                </GlassCard>
              ))}
            </div>
          ) : <em className="text-white/40">—</em>}
        </Sec>
        {s.quote?.text && (
          <Sec title={isNl ? "13 · Hotel manager" : "13 · Hotel manager"}>
            <div className="rounded-2xl border border-bht-gold/30 bg-bht-gold/5 p-8">
              <Quotes size={22} weight="fill" className="mb-4 text-bht-gold" />
              <p className="font-serif text-2xl italic leading-snug text-white">"{s.quote.text}"</p>
              <div className="mt-4 text-sm text-white/70">— {s.quote.author}, <span className="text-white/50">{s.quote.role}</span></div>
            </div>
          </Sec>
        )}
        <Sec title={isNl ? "14 · Toekomstige roadmap" : "14 · Future roadmap"}>{s.roadmap || <em className="text-white/40">—</em>}</Sec>
      </div>

      <section className="border-t border-white/[0.06] py-32">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <SectionHeading align="center" eyebrow={isNl ? "Klaar voor uw hotel?" : "Ready for your hotel?"} title={isNl ? "Wordt onze volgende case study." : "Become our next case study."} />
          <div className="mt-10"><GoldButton to={`/${lang}/book-demo`}>{isNl ? "Demo aanvragen" : "Book a demo"}</GoldButton></div>
        </div>
      </section>
    </>
  );
}
